import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, Radio, FormControlLabel, RadioGroup } from "@mui/material";
import { FiEdit2 } from "react-icons/fi";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import StripeCardForm from "../StripeCardForm";
import BillingService from "../../../../services/billing.service";
import { useAppState } from "../../../../context/StateContext";
import env from "../../../../util/env";

interface PaymentMethod {
  id: number;
  last4: string;
  brand: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

interface PaymentMethodSectionProps {
  paymentMethods: PaymentMethod[];
  onPaymentMethodAdded: (paymentMethodData?: any) => void;
  onPaymentMethodSelected?: (method: string) => void;
  companyId: number;
}

const PaymentMethodSection: React.FC<PaymentMethodSectionProps> = ({
  paymentMethods,
  onPaymentMethodAdded,
  onPaymentMethodSelected,
  companyId,
}) => {
  const { currentUser } = useAppState();
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<string>("credit_card");
  const [selectedAlternativeMethod, setSelectedAlternativeMethod] = useState<string>("");
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const defaultPaymentMethod = paymentMethods.find((pm) => pm.isDefault);

  const [setupIntentAttempted, setSetupIntentAttempted] = useState(false);

  useEffect(() => {
    const stripeKey = env.STRIPE_PUBLISHABLE_KEY || (window as any).__STRIPE_PUBLISHABLE_KEY__;

    if (stripeKey) {
      setStripeLoading(true);
      loadStripe(stripeKey)
        .then((stripe) => {
          setStripePromise(stripe);
          setStripeLoading(false);
        })
        .catch((error) => {
          console.error("Error loading Stripe:", error);
          setStripePromise(null);
          setStripeLoading(false);
        });
    } else {
      console.error("Stripe key not found");
      setStripeLoading(false);
    }
  }, []);

  const handleCreateSetupIntent = useCallback(async () => {
    if (!currentUser?.companyId) return;

    setLoading(true);
    setClientSecret("");
    try {
      const result = await BillingService.createSetupIntent(currentUser.companyId);
      if (result && result.clientSecret) {
        setClientSecret(result.clientSecret);
      } else {
        console.error("Invalid result format:", result);
        setClientSecret("");
        setSetupIntentAttempted(false);
      }
    } catch (error: any) {
      console.error("Error creating setup intent:", error);
      setClientSecret("");
      setSetupIntentAttempted(false);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.companyId]);

  useEffect(() => {
    if (
      selectedMethod === "credit_card" &&
      showAddForm &&
      !clientSecret &&
      !loading &&
      !setupIntentAttempted &&
      currentUser?.companyId
    ) {
      setSetupIntentAttempted(true);
      handleCreateSetupIntent();
    }
  }, [
    selectedMethod,
    showAddForm,
    clientSecret,
    loading,
    setupIntentAttempted,
    currentUser?.companyId,
    handleCreateSetupIntent,
  ]);

  const resetFormState = () => {
    setClientSecret("");
    setLoading(false);
    setSelectedMethod("credit_card");
    setSetupIntentAttempted(false);
    setSelectedAlternativeMethod("");
  };

  const handleCancel = () => {
    resetFormState();
    setShowAddForm(false);
    setSelectedAlternativeMethod("");
    // Notificar que nenhum método foi selecionado quando cancela
    if (onPaymentMethodSelected) {
      onPaymentMethodSelected("");
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        sx={{
          fontFamily: "Manrope",
          fontSize: "18px",
          fontWeight: 600,
          lineHeight: "24px",
          color: "#101828",
          mb: 3,
        }}
      >
        Payment method
      </Typography>

      {!showAddForm && paymentMethods.length === 0 && !selectedAlternativeMethod && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            bgcolor: "#F9FAFB",
            borderRadius: "8px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Manrope",
              fontSize: "14px",
              lineHeight: "20px",
              color: "#667085",
            }}
          >
            No payment method registered
          </Typography>
          <Button
            onClick={() => {
              resetFormState();
              setSelectedAlternativeMethod("");
              if (onPaymentMethodSelected) {
                onPaymentMethodSelected("");
              }
              setShowAddForm(true);
            }}
            sx={{
              fontFamily: "Manrope",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "20px",
              color: "#0055A4",
              textTransform: "none",
              "&:hover": {
                bgcolor: "transparent",
                color: "#003d7a",
              },
            }}
          >
            + Add a payment method
          </Button>
        </Box>
      )}

      {!showAddForm && defaultPaymentMethod && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            border: "1px solid #D0D5DD",
            borderRadius: "8px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{
                fontFamily: "Manrope",
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "20px",
                color: "#344054",
              }}
            >
              Credit Card
            </Typography>
            <Typography
              sx={{
                fontFamily: "Manrope",
                fontSize: "14px",
                lineHeight: "20px",
                color: "#667085",
              }}
            >
              **** **** **** {defaultPaymentMethod.last4}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Manrope",
                fontSize: "12px",
                fontWeight: 700,
                lineHeight: "18px",
                color: "#344054",
                textTransform: "uppercase",
              }}
            >
              {defaultPaymentMethod.brand}
            </Typography>
          </Box>
          <Button
            onClick={() => {
              resetFormState();
              setSelectedAlternativeMethod("");
              if (onPaymentMethodSelected) {
                onPaymentMethodSelected("");
              }
              setShowAddForm(true);
            }}
            startIcon={<FiEdit2 />}
            sx={{
              fontFamily: "Manrope",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "20px",
              color: "#0055A4",
              textTransform: "none",
              "&:hover": {
                bgcolor: "transparent",
                color: "#003d7a",
              },
            }}
          >
            Edit
          </Button>
        </Box>
      )}

      {showAddForm && (
        <Box
          sx={{
            border: "1px solid #D0D5DD",
            borderRadius: "8px",
            p: 3,
          }}
        >
          <RadioGroup
            value={selectedMethod}
            onChange={(e) => {
              const method = e.target.value;
              setSelectedMethod(method);

              // Se selecionou um método alternativo (direct_debit ou invoice), fechar o formulário e notificar
              if (method === "direct_debit" || method === "invoice") {
                setSelectedAlternativeMethod(method);
                setShowAddForm(false);
                if (onPaymentMethodSelected) {
                  onPaymentMethodSelected(method);
                }
              } else if (onPaymentMethodSelected) {
                onPaymentMethodSelected(method);
              }
            }}
          >
            <FormControlLabel
              value="credit_card"
              control={
                <Radio
                  sx={{
                    color: "#D0D5DD",
                    "&.Mui-checked": {
                      color: "#0055A4",
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: "Manrope",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "20px",
                    color: "#344054",
                  }}
                >
                  Credit card
                </Typography>
              }
            />

            {selectedMethod === "credit_card" && (
              <Box sx={{ mt: 2, pl: 4 }}>
                {stripeLoading ? (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography sx={{ color: "#667085", fontSize: "14px" }}>
                      Loading Stripe...
                    </Typography>
                  </Box>
                ) : !stripePromise ? (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography sx={{ color: "#D92D20", fontSize: "14px" }}>
                      Error: Stripe failed to load. Please refresh the page.
                    </Typography>
                  </Box>
                ) : loading ? (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography sx={{ color: "#667085", fontSize: "14px" }}>
                      Loading payment form...
                    </Typography>
                  </Box>
                ) : !clientSecret ? (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography sx={{ color: "#D92D20", fontSize: "14px" }}>
                      Error loading payment form. Please try again.
                    </Typography>
                    <Button
                      onClick={handleCreateSetupIntent}
                      sx={{
                        mt: 1,
                        fontFamily: "Manrope",
                        fontSize: "14px",
                        fontWeight: 600,
                        lineHeight: "20px",
                        color: "#0055A4",
                        textTransform: "none",
                        bgcolor: "transparent",
                        border: "1px solid #0055A4",
                        borderRadius: "8px",
                        "&:hover": {
                          bgcolor: "#F0F9FF",
                        },
                      }}
                    >
                      Retry
                    </Button>
                  </Box>
                ) : stripePromise ? (
                  <Elements stripe={stripePromise} key={clientSecret}>
                    <StripeCardForm
                      clientSecret={clientSecret}
                      defaultEmail={currentUser?.email}
                      defaultName={currentUser?.name}
                      onSaved={async (paymentMethodId, brand, last4) => {
                        if (!paymentMethodId) {
                          return;
                        }

                        try {
                          const requestData = {
                            paymentMethodId,
                            companyId: currentUser?.companyId || 0,
                            nameOnCard: currentUser?.name || "",
                            billingAddress: currentUser?.company?.location?.longAddress || "",
                          };

                          const result = await BillingService.attachPaymentMethod(requestData);

                          if (result.success) {
                            setShowAddForm(false);
                            onPaymentMethodAdded(result.paymentMethod);
                            if (onPaymentMethodSelected) {
                              onPaymentMethodSelected("credit_card");
                            }
                          }
                        } catch (error: any) {
                          // Se customer não existir, criar automaticamente
                          if (
                            error?.response?.data?.error === "CUSTOMER_NOT_FOUND" ||
                            error?.response?.status === 404
                          ) {
                            try {
                              // Preparar dados da company para criar customer no Stripe
                              const companyData = {
                                id: companyId,
                                name: currentUser?.company?.name || "",
                                email: currentUser?.company?.email || currentUser?.email || "",
                                profileId: currentUser?.company?.profileId || 0,
                              };

                              await BillingService.createCustomer(companyData, currentUser?.id);

                              // Aguardar um momento para garantir que o banco foi atualizado
                              await new Promise((resolve) => setTimeout(resolve, 1000));

                              // Tentar novamente anexar o payment method
                              const requestData = {
                                paymentMethodId,
                                companyId: currentUser?.companyId || 0,
                                nameOnCard: currentUser?.name || "",
                                billingAddress: currentUser?.company?.location?.longAddress || "",
                              };

                              const result = await BillingService.attachPaymentMethod(requestData);

                              if (result.success) {
                                setShowAddForm(false);
                                onPaymentMethodAdded(result.paymentMethod);
                                if (onPaymentMethodSelected) {
                                  onPaymentMethodSelected("credit_card");
                                }
                              }
                            } catch (createError: any) {
                              console.error("Error creating customer:", createError);
                              throw new Error(
                                createError?.response?.data?.message || "Error creating customer"
                              );
                            }
                          } else {
                            throw error;
                          }
                        }
                      }}
                      onCancel={handleCancel}
                    />
                  </Elements>
                ) : (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography sx={{ color: "#D92D20", fontSize: "14px" }}>
                      Error: Stripe not initialized. Please refresh the page.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <FormControlLabel
              value="direct_debit"
              control={
                <Radio
                  sx={{
                    color: "#D0D5DD",
                    "&.Mui-checked": {
                      color: "#0055A4",
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: "Manrope",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "20px",
                    color: "#344054",
                  }}
                >
                  Direct Debit (30 days)
                </Typography>
              }
              sx={{ mt: 2 }}
            />

            <FormControlLabel
              value="invoice"
              control={
                <Radio
                  sx={{
                    color: "#D0D5DD",
                    "&.Mui-checked": {
                      color: "#0055A4",
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: "Manrope",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "20px",
                    color: "#344054",
                  }}
                >
                  Invoice 30 days
                </Typography>
              }
              sx={{ mt: 2 }}
            />
          </RadioGroup>
        </Box>
      )}

      {!showAddForm && selectedAlternativeMethod && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            border: "1px solid #D0D5DD",
            borderRadius: "8px",
            bgcolor: "#F9FAFB",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Manrope",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "20px",
              color: "#344054",
            }}
          >
            {selectedAlternativeMethod === "direct_debit"
              ? "Direct Debit (30 days)"
              : "Invoice 30 days"}
          </Typography>
          <Button
            onClick={() => {
              resetFormState();
              setSelectedAlternativeMethod("");
              if (onPaymentMethodSelected) {
                onPaymentMethodSelected("");
              }
              setShowAddForm(true);
            }}
            startIcon={<FiEdit2 />}
            sx={{
              fontFamily: "Manrope",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "20px",
              color: "#0055A4",
              textTransform: "none",
              "&:hover": {
                bgcolor: "transparent",
                color: "#003d7a",
              },
            }}
          >
            Change
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PaymentMethodSection;
